import React, { useContext, useEffect, useMemo, useState } from 'react'
import axios from '../../../../config/axios'
import { Eye, Pencil, Power, Copy, ArrowLeftRight, X, Trash2 } from 'lucide-react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import { useTenant } from '../../../../contexts/TenantContext'
import ApiService from '../../../../services/ApiService'
import ModalContext from '../../../../contexts/ModalContext'
import StockContext from '../../../../contexts/StockContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { buildResourceColumns, resourceScreenKey, Resource } from './resourceColumns'

interface ContextType {
  search: string;
  appliedFilters?: Filter[];
  refresh?: boolean;
}

const TAKE = 50

const ResourcesTable: React.FC = () => {
  const { isFashionTenant } = useTenant()
  const [resources, setResources] = useState<Resource[]>([])
  const [total, setTotal] = useState<number>(0)
  const [skip, setSkip] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [localRefresh, setLocalRefresh] = useState(0)
  const [statusTarget, setStatusTarget] = useState<Resource | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkInactivateOpen, setBulkInactivateOpen] = useState(false)
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string | number>>(new Set())
  const [bulkDraft, setBulkDraft] = useState<Record<string, any>>({})
  const [isBulkSaving, setIsBulkSaving] = useState(false)
  const { setModalActive, setModalSection, setItIsUpdate } = useContext(ModalContext)
  const { setStock, refresh: stockContextRefresh } = useContext(StockContext)
  const { setFeedback } = useContext(FeedbackContext)
  const itemName = isFashionTenant ? 'Aviamento' : 'Insumo'

  const columns = useMemo(() => buildResourceColumns(isFashionTenant), [isFashionTenant])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    resourceScreenKey(isFashionTenant),
    columns,
  )

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const resourceIdParam = searchParams.get('resourceId') || searchParams.get('id')

  useEffect(() => {
    if (resourceIdParam) {
      const parsed = Number(resourceIdParam)
      if (!Number.isNaN(parsed) && parsed > 0) {
        navigate(`edit?id=${parsed}`)
      }
    }
  }, [resourceIdParam, navigate])

  const context = useOutletContext<ContextType>() || { search: '', appliedFilters: [], refresh: false };
  const { search, appliedFilters = [], refresh } = context;

  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    const getResources = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`, {
          params: {
            search: debouncedSearch,
            skip: 0,
            take: TAKE,
            filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
          }
        })
        if (response.data) {
          const items = response.data.data ?? response.data
          const totalItems = response.data.total ?? (Array.isArray(response.data) ? response.data.length : 0)

          setResources(items)
          setTotal(totalItems)
          setSkip(TAKE)
        }
      } catch (error) {
        console.error("Erro ao buscar recursos:", error);
      } finally {
        setIsLoading(false)
      }
    }
    getResources()
  }, [debouncedSearch, appliedFilters, localRefresh, refresh, stockContextRefresh])

  const handleConfirmStatusChange = async () => {
    if (!statusTarget) return
    const newStatus = statusTarget.isActivated === false
    try {
      await ApiService.resources.update(Number(statusTarget.id), { isActivated: newStatus })
      setFeedback({
        message: `${itemName} ${newStatus ? 'ativado' : 'inativado'} com sucesso!`,
        isOpen: true,
        statusOk: true,
      })
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || `Erro ao ${newStatus ? 'ativar' : 'inativar'} ${itemName.toLowerCase()}`
      setFeedback({ message: errorMessage, isOpen: true, statusOk: false })
    } finally {
      setStatusTarget(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await ApiService.resources.delete(Number(deleteTarget.id))
      setFeedback({
        message: `${itemName} excluído com sucesso!`,
        isOpen: true,
        statusOk: true,
      })
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || `Erro ao excluir ${itemName.toLowerCase()}`
      setFeedback({
        message: Array.isArray(errorMessage) ? errorMessage[0] : errorMessage,
        isOpen: true,
        statusOk: false,
      })
    } finally {
      setDeleteTarget(null)
    }
  }

  const fetchAllResourceRows = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`, {
        params: {
          search: debouncedSearch,
          skip: 0,
          take: 100000,
          filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
        },
      })
      const items = response?.data?.data ?? response?.data ?? []
      return Array.isArray(items) ? items : []
    } catch {
      return resources
    }
  }

  const fetchAllResourceRecords = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`, {
        params: {
          skip: 0,
          take: 100000,
        },
      })
      const items = response?.data?.data ?? response?.data ?? []
      return Array.isArray(items) ? items : []
    } catch {
      return resources
    }
  }

  const handleConfirmBulkDelete = async () => {
    if (selectedRowIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const ids = Array.from(selectedRowIds)
      for (const id of ids) {
        await ApiService.resources.delete(Number(id))
      }
      setFeedback({
        isOpen: true,
        message: `${ids.length} ${itemName.toLowerCase()}(s) excluído(s) com sucesso!`,
        statusOk: true,
      })
      setSelectedRowIds(new Set())
      setBulkDraft({})
      setLocalRefresh((prev) => prev + 1)
    } catch {
      setFeedback({
        isOpen: true,
        message: `Erro ao excluir ${itemName.toLowerCase()}(s) selecionado(s).`,
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
      setBulkDeleteOpen(false)
    }
  }

  const handleConfirmBulkInactivate = async () => {
    if (selectedRowIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const ids = Array.from(selectedRowIds)
      for (const id of ids) {
        await ApiService.resources.update(Number(id), { isActivated: false })
      }
      setFeedback({
        isOpen: true,
        message: `${ids.length} ${itemName.toLowerCase()}(s) inativado(s) com sucesso!`,
        statusOk: true,
      })
      setSelectedRowIds(new Set())
      setBulkDraft({})
      setLocalRefresh((prev) => prev + 1)
    } catch {
      setFeedback({
        isOpen: true,
        message: `Erro ao inativar ${itemName.toLowerCase()}(s) selecionado(s).`,
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
      setBulkInactivateOpen(false)
    }
  }

  const handleNewMovement = (resource: Resource) => {
    setStock({
      code: resource.code,
      codeId: resource.codeId,
      description: resource.name,
      totalWeight: resource.totalWeight != null ? String(resource.totalWeight) : '0',
      lengthInMm: resource.lengthInMm != null ? String(resource.lengthInMm) : '0',
      unitQuantity: (resource as any).unitQuantity != null ? String((resource as any).unitQuantity) : '0',
      stockUnit: resource.stockUnit,
      isResource: true,
      id: resource.id,
      resourceId: resource.id,
    } as any)
    setItIsUpdate(true)
    setModalSection('modalEntryOrExit')
    setModalActive(true)
  }

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`,
        {
          params: {
            search: debouncedSearch,
            skip,
            take: TAKE,
            filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
          }
        },
      )
      if (response.data) {
        const newItems: Resource[] = response.data.data ?? response.data
        const totalItems = response.data.total ?? (Array.isArray(response.data) ? response.data.length : 0)

        setResources(prev => [...prev, ...newItems])
        setTotal(totalItems)
        setSkip(prev => prev + TAKE)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const getCompanyLabel = (item: any): string => {
    if (!item) return ''
    const baseName = (item.companyName || item.contactName || item.name || '').toString().trim()
    if (!baseName) return ''
    if (!item.isMain) return baseName
    const tipo = item.tipoEstabelecimento || (item.cnpj ? (item.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')
    return tipo ? `${baseName} - ${tipo}` : baseName
  }

  const handleSelectOption = (colId: string, option: any) => {
    const idField = `${colId}Id`
    if (!option) {
      setBulkDraft((prev) => {
        const next = { ...prev }
        delete next[colId]
        delete next[idField]
        return next
      })
      return
    }
    if (colId === 'ncm') {
      setBulkDraft((prev) => ({ ...prev, ncm: option.ncm ?? option.name, ncmId: option.id }))
      return
    }
    const isSupplierField = ['supplier', 'suppliers'].includes(colId)
    const name = getCompanyLabel(option) || option.name || option.type || option.companyName || String(option.id)
    if (isSupplierField) {
      setBulkDraft((prev) => {
        const currentList: Array<{ id: any; name: string }> = Array.isArray(prev.suppliers)
          ? prev.suppliers
          : typeof prev[colId] === 'string' && prev[colId].trim()
            ? prev[colId].split(',').map((s: string) => ({ id: s.trim(), name: s.trim() })).filter((s: any) => s.name)
            : []
        const nextList = currentList.some((s) => s.name === name || s.id === option.id)
          ? currentList
          : [...currentList, { id: option.id, name }]
        return {
          ...prev,
          [colId]: nextList.map((s) => s.name).join(', '),
          suppliers: nextList,
        }
      })
      return
    }
    if (colId === 'category') {
      setBulkDraft((prev) => ({
        ...prev,
        category: name,
        categoryId: option ? option.id : undefined,
        type: '',
        typeId: undefined,
      }))
      return
    }

    setBulkDraft((prev) => ({
      ...prev,
      [colId]: name,
      ...(option.id ? { [idField]: option.id } : {}),
    }))
  }

  const handleSaveBulkEdits = async () => {
    if (selectedRowIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const selectedIds = Array.from(selectedRowIds)
      const payload: any = {}
      if (bulkDraft.name) payload.name = bulkDraft.name
      if (bulkDraft.code) payload.code = /^[\d\s]+$/.test(String(bulkDraft.code).trim()) ? String(bulkDraft.code).replace(/\s+/g, '') : String(bulkDraft.code).trim()
      if (bulkDraft.description) payload.description = bulkDraft.description
      if (bulkDraft.categoryId) payload.categoryId = Number(bulkDraft.categoryId)
      if (bulkDraft.typeId) payload.typeId = Number(bulkDraft.typeId)
      if (bulkDraft.material) payload.material = bulkDraft.material
      if (bulkDraft.format) payload.format = bulkDraft.format
      if (bulkDraft.fabrication) payload.fabrication = bulkDraft.fabrication
      if (bulkDraft.shelf) payload.shelf = bulkDraft.shelf
      if (bulkDraft.stockLocation) payload.stockLocation = bulkDraft.stockLocation
      if (bulkDraft.stockStreet) payload.stockStreet = bulkDraft.stockStreet
      if (bulkDraft.ncmId) payload.ncmId = Number(bulkDraft.ncmId)
      if (bulkDraft.stockUnit) payload.stockUnit = bulkDraft.stockUnit
      if (bulkDraft.totalWeight) payload.totalWeight = Number(bulkDraft.totalWeight)
      if (bulkDraft.lengthInMm) payload.lengthInMm = Number(bulkDraft.lengthInMm)
      if (bulkDraft.unitQuantity) payload.unitQuantity = Number(bulkDraft.unitQuantity)
      if (bulkDraft.dimension1) payload.dimension1 = Number(bulkDraft.dimension1)
      if (bulkDraft.dimension2) payload.dimension2 = Number(bulkDraft.dimension2)
      if (bulkDraft.isActivated) payload.isActivated = bulkDraft.isActivated === 'Ativo'
      if (bulkDraft.color || bulkDraft.colorId) {
        payload.colors = [{ name: bulkDraft.color, ...(bulkDraft.colorId ? { id: Number(bulkDraft.colorId) } : {}) }]
      }
      if (bulkDraft.suppliers && Array.isArray(bulkDraft.suppliers)) {
        payload.suppliers = bulkDraft.suppliers.map((s: any) => (typeof s === 'object' && s.id ? s.id : s))
      }

      await Promise.all(selectedIds.map((id) => ApiService.resources.update(Number(id), payload)))

      setFeedback({
        message: `${selectedIds.length} ${itemName.toLowerCase()}(s) atualizado(s) com sucesso!`,
        isOpen: true,
        statusOk: true,
      })
      setSelectedRowIds(new Set())
      setBulkDraft({})
      setLocalRefresh((prev) => prev + 1)
    } catch (error: any) {
      console.error(error)
      setFeedback({
        message: 'Erro ao processar alteração em massa.',
        isOpen: true,
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
    }
  }

  const isUniqueOrReadOnlyColumn = (colId: string) => ['prices', 'code'].includes(colId)

  const renderBulkInput = (colId: string) => {
    const value = bulkDraft[colId] || ''
    const idField = `${colId}Id`
    const isSupplierField = colId === 'suppliers'
    const selectedOption = value && !isSupplierField ? { id: bulkDraft[idField] || value, name: String(value) } : null

    if (isUniqueOrReadOnlyColumn(colId)) {
      return null
    }

    if (colId === 'isActivated') {
      return (
        <select
          value={value}
          onChange={(e) => setBulkDraft((prev) => ({ ...prev, isActivated: e.target.value }))}
          style={{
            width: '100%',
            padding: '4px 6px',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid #0090d3',
            backgroundColor: '#fff',
            cursor: 'pointer',
          }}
        >
          <option value="">(Sem alteração)</option>
          <option value="Ativo">Ativo</option>
          <option value="Inativo">Inativo</option>
        </select>
      )
    }

    if (colId === 'code' || colId === 'quantities' || colId === 'prices' || colId.startsWith('quantities_') || colId.startsWith('prices_')) {
      return null
    }

    const isLookup = [
      'category',
      'type',
      'color',
      'material',
      'format',
      'fabrication',
      'shelf',
      'stockLocation',
      'stockStreet',
      'ncm',
      'suppliers',
    ].includes(colId)

    if (isLookup) {
      const rawSupplierList: Array<{ id: any; name: string }> = isSupplierField
        ? Array.isArray(bulkDraft.suppliers)
          ? bulkDraft.suppliers
          : typeof value === 'string' && value.trim()
            ? value.split(',').map((s: string) => ({ id: s.trim(), name: s.trim() })).filter((s: any) => s.name)
            : []
        : []

      const isTypeField = colId === 'type'
      const hasCategory = Boolean(bulkDraft.categoryId)

      return (
        <div style={{ minWidth: '140px', maxWidth: '240px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <InfiniteSelect
            key={`${colId}-${bulkDraft.categoryId || 'none'}`}
            mode="crud"
            value={selectedOption}
            categoryId={bulkDraft.categoryId}
            placeholder={
              isTypeField && !hasCategory
                ? 'Selecione uma categoria primeiro'
                : isSupplierField
                  ? rawSupplierList.length > 0
                    ? '+ Adicionar...'
                    : 'Selecionar...'
                  : 'Alterar...'
            }
            searchPlaceholder="Buscar ou criar..."
            disabled={isTypeField && !hasCategory}
            onSelect={(option) => handleSelectOption(colId, option)}
            errorOptionsMessage={colId === 'ncm' ? 'Código não existe na tabela oficial de NCM' : undefined}
            showPantoneSwatch={false}
            fetchFunction={async (_limit, _offset, searchVal) => {
              if (colId === 'ncm') {
                const response = await ApiService.stockLookup.searchNcmCatalogo(searchVal, _limit, _offset)
                const items = Array.isArray(response?.data) ? response.data : []
                return items.map((item: any) => ({
                  id: item.id,
                  name: `${item.ncm}${item.descricao ? ` · ${item.descricao}` : ''}${item.jaCadastrado ? ' · em uso' : ''}`,
                  ncm: item.ncm,
                  descricao: item.descricao,
                  jaCadastrado: item.jaCadastrado,
                }))
              }
              let response: any
              if (colId === 'category') {
                response = await ApiService.resources.getCategories({ query: searchVal })
              } else if (colId === 'type') {
                if (!bulkDraft.categoryId) return []
                response = await ApiService.resources.getTypes({ categoryId: bulkDraft.categoryId, query: searchVal })
              } else if (colId === 'color') {
                response = await ApiService.resources.getColors()
              } else if (colId === 'material') {
                response = await ApiService.stockLookup.getMaterialsForResource()
              } else if (colId === 'format') {
                response = await ApiService.stockLookup.getFormats()
              } else if (colId === 'fabrication') {
                response = await ApiService.stockLookup.getFabrications()
              } else if (colId === 'shelf') {
                response = await ApiService.stockLookup.getShelfs()
              } else if (colId === 'stockLocation') {
                response = await ApiService.stockLookup.getStockLocations()
              } else if (colId === 'stockStreet') {
                response = await ApiService.stockLookup.getStockStreets()
              } else if (colId === 'suppliers') {
                response = await ApiService.companies.findAll({ query: searchVal || '', companyType: 'Fornecedor' })
              }

              const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
              const mapped = items
                .map((item: any, idx: number) => {
                  if (typeof item === 'string') return { id: item, name: item }
                  const name = getCompanyLabel(item) || item.name || item.type || item.ncm || item.companyName || ''
                  return {
                    id: item.id || name || `temp-${idx}`,
                    name: name,
                  }
                })
                .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')

              return searchVal
                ? mapped.filter((item: any) => item.name?.toLowerCase().includes(searchVal.toLowerCase()))
                : mapped
            }}
            onItemCreate={async (name: string) => {
              let response: any
              const trimmedName = name.trim()
              if (colId === 'category') {
                response = await ApiService.resources.createCategory({ name: trimmedName })
              } else if (colId === 'type') {
                if (!bulkDraft.categoryId) throw new Error('Selecione uma categoria primeiro')
                response = await ApiService.resources.createType({ name: trimmedName, categoryId: bulkDraft.categoryId })
              } else if (colId === 'color') {
                response = await ApiService.resources.createColor({ name: trimmedName })
              } else if (colId === 'material') {
                response = await ApiService.stockLookup.createMaterial({ name: trimmedName, type: 'RESOURCE' })
              } else if (colId === 'format') {
                response = await ApiService.stockLookup.createFormat({ name: trimmedName })
              } else if (colId === 'fabrication') {
                response = await ApiService.stockLookup.createFabrication({ name: trimmedName })
              } else if (colId === 'shelf') {
                response = await ApiService.stockLookup.createShelf({ name: trimmedName })
              } else if (colId === 'stockLocation') {
                response = await ApiService.stockLookup.createStockLocation({ name: trimmedName })
              } else if (colId === 'stockStreet') {
                response = await ApiService.stockLookup.createStockStreet({ name: trimmedName })
              }
              const resData = response?.data || {}
              return { id: resData.id || trimmedName, name: resData.name || resData.type || trimmedName }
            }}
            onItemDelete={async (id: any) => {
              if (colId === 'category') {
                await ApiService.resources.deleteCategory(id)
              } else if (colId === 'type') {
                await ApiService.resources.deleteType(id)
              } else if (colId === 'color') {
                await ApiService.resources.deleteColor(id)
              } else if (colId === 'material') {
                await ApiService.stockLookup.deleteMaterial(id)
              } else if (colId === 'format') {
                await ApiService.stockLookup.deleteFormat(id)
              } else if (colId === 'fabrication') {
                await ApiService.stockLookup.deleteFabrication(id)
              } else if (colId === 'shelf') {
                await ApiService.stockLookup.deleteShelf(id)
              } else if (colId === 'stockLocation') {
                await ApiService.stockLookup.deleteStockLocation(id)
              } else if (colId === 'stockStreet') {
                await ApiService.stockLookup.deleteStockStreet(id)
              }
            }}
            allowCreate={isTypeField ? hasCategory : (colId !== 'suppliers' && colId !== 'ncm')}
            allowDelete={isTypeField ? hasCategory : (colId !== 'suppliers' && colId !== 'ncm')}
            showCreateOnlyIfNotExists={true}
            minCreateLength={2}
          />
          {isSupplierField && rawSupplierList.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxHeight: '60px', overflowY: 'auto' }}>
              {rawSupplierList.map((sup, idx) => (
                <span
                  key={idx}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    fontSize: '10px',
                    fontWeight: 500,
                    padding: '1px 5px',
                    borderRadius: '10px',
                    border: '1px solid #bae6fd',
                  }}
                >
                  {sup.name}
                  <X
                    size={10}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const nextList = rawSupplierList.filter((_, i) => i !== idx)
                      setBulkDraft((prev) => ({
                        ...prev,
                        suppliers: nextList,
                      }))
                    }}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <input
        type="text"
        placeholder="Alterar..."
        value={value}
        onChange={(e) => setBulkDraft((prev) => ({ ...prev, [colId]: e.target.value }))}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: '11px',
          borderRadius: '4px',
          border: '1px solid #0090d3',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
        }}
      />
    )
  }

  return (
    <>
      <StandardDataTable
        tableName="Resource"
        columns={columns}
        data={resources}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={total}
        onLoadMore={handleLoadMore}
        hasMore={resources.length < total}
        isLoading={isLoading || isLoadingMore}
        countLabel={isFashionTenant ? 'aviamentos' : 'insumos'}
        exportFileName={isFashionTenant ? 'aviamentos' : 'insumos'}
        exportSheetName={isFashionTenant ? 'Aviamentos' : 'Insumos'}
        exportRows={fetchAllResourceRows}
        exportAllRows={fetchAllResourceRecords}
        onBulkDelete={() => setBulkDeleteOpen(true)}
        onBulkInactivate={() => setBulkInactivateOpen(true)}
        rowClassName={(resource) => (resource.isActivated === false ? 'yellowLine' : '')}
        selectable={true}
        selectedRowIds={selectedRowIds}
        onSelectRow={(id, sel) => {
          setSelectedRowIds((prev) => {
            const next = new Set(prev)
            if (sel) next.add(id)
            else next.delete(id)
            return next
          })
        }}
        onSelectAllRows={(sel) => {
          if (sel) {
            setSelectedRowIds(new Set(resources.map((r) => r.id)))
          } else {
            setSelectedRowIds(new Set())
            setBulkDraft({})
          }
        }}
        bulkConfig={{
          bulkDraft,
          onBulkDraftChange: (field, val) => setBulkDraft((prev) => ({ ...prev, [field]: val })),
          onSave: handleSaveBulkEdits,
          isSaving: isBulkSaving,
          selectedCountLabel: isFashionTenant ? 'aviamento(s)' : 'insumo(s)',
          onClearSelection: () => {
            setSelectedRowIds(new Set())
            setBulkDraft({})
          },
          renderBulkInput,
        }}
        rowActions={(resource) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => navigate(`view?id=${resource.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`edit?id=${resource.id}`) },
          ...(isFashionTenant
            ? [{ label: 'Criar Variação', icon: <Copy size={16} />, onClick: () => navigate(`create?sourceId=${resource.id}&variation=true`) }]
            : []),
          ...(resource.codeId
            ? [{ label: 'Novo movimento', icon: <ArrowLeftRight size={16} />, onClick: () => handleNewMovement(resource) }]
            : []),
          {
            label: resource.isActivated === false ? 'Ativar' : 'Inativar',
            icon: <Power size={16} />,
            onClick: () => setStatusTarget(resource),
          },
          {
            label: 'Excluir',
            icon: <Trash2 size={16} />,
            onClick: () => setDeleteTarget(resource),
          },
        ]}
      />

      <ConfirmationModal
        isOpen={!!statusTarget}
        title={statusTarget?.isActivated === false ? `Ativar ${itemName}` : `Inativar ${itemName}`}
        message={
          <div>
            <p>Tem certeza que deseja {statusTarget?.isActivated === false ? 'ativar' : 'inativar'} o {itemName.toLowerCase()}: &quot;{statusTarget?.name}&quot;?</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setStatusTarget(null), variant: 'outline' },
          {
            label: statusTarget?.isActivated === false ? 'Ativar' : 'Inativar',
            onClick: handleConfirmStatusChange,
            variant: statusTarget?.isActivated === false ? 'primary' : 'danger',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title={`Excluir ${itemName}`}
        message={
          <div>
            <p>Tem certeza que deseja excluir o {itemName.toLowerCase()}: &quot;{deleteTarget?.name}&quot;? Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setDeleteTarget(null), variant: 'outline' },
          {
            label: 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={bulkDeleteOpen}
        title={`Excluir ${itemName}(s) em massa`}
        message={
          <div>
            <p>Tem certeza que deseja excluir os {selectedRowIds.size} {itemName.toLowerCase()}(s) selecionado(s)? Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setBulkDeleteOpen(false), variant: 'outline' },
          {
            label: 'Excluir',
            onClick: handleConfirmBulkDelete,
            variant: 'danger',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={bulkInactivateOpen}
        title={`Inativar ${itemName}(s) em massa`}
        message={
          <div>
            <p>Tem certeza que deseja inativar os {selectedRowIds.size} {itemName.toLowerCase()}(s) selecionado(s)?</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setBulkInactivateOpen(false), variant: 'outline' },
          {
            label: 'Inativar',
            onClick: handleConfirmBulkInactivate,
            variant: 'danger',
          },
        ]}
      />
    </>
  )
}

export default ResourcesTable
